import React, {useEffect} from 'react';
import {Form, Modal} from 'react-bootstrap';
import {ComponentUtils, DetailActions, notification, SwEmbeddedDetail, SwForm, SwList} from 'simplifyworks-core-ui';
import i18n from 'i18next';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {getOrganOfferRecipientsInitialValue} from '../../rest';
import {
	CountryWithFlag,
	IndeterminateCheckbox,
	OrganizationsList,
	SelectedOrganizationsList
} from '../renderers/renderers';
import {useOrganOfferHandlers} from '../../hooks/useOrganOfferHandlers';
import {getElementAfter} from '../../utils';

const ChangeRecipientsModal = (props) => {
	const changeSelectedOrganizationsValue = (value) => {
		props.actions.detail.changeValue(
			'/api/foedus/organ-offer/change-recipients/new',
			'DEFAULT',
			'selectedOrganizations',
			value
		);
	};

	const {
		onCheckedAllChange,
		onCheckedAllGlobalGroupsChange,
		onCheckedAllPrivateGroupsChange,
		onCountryCheckedChange,
		onOrganizationCheckedChange,
		onGlobalGroupCheckedChange,
		onPrivateGroupCheckedChange,
		getSelectedOrganizationsInCountryCount,
		getTotalOrganizationsInCountryCount,
		getSelectedOrganizationsInGlobalGroupCount,
		getTotalOrganizationsInGlobalGroupCount,
		getSelectedOrganizationsInPrivateGroupCount,
		getTotalOrganizationsInPrivateGroupCount
	} = useOrganOfferHandlers({
		newOrganOfferFormData: props?.newOrganOfferFormData,
		changeSelectedOrganizationsValue,
		loggedUserPrimaryFoedusRole: props?.loggedUserPrimaryFoedusRole
	});

	useEffect(() => {
		if (props.show) {
			getOrganOfferRecipientsInitialValue(
				{},
				(res) => {
					props.actions.detail.formInit(
						'/api/foedus/organ-offer/change-recipients/new',
						'DEFAULT',
						null,
						null,
						{
							entryId: props?.entryId,
							definitionId: props?.definitionId,
							notificationGroups: {
								countries: res?.countries?.resources,
								organizations: res?.organizations?.resources,
								globalGroups: res?.globalGroups,
								privateGroups: res?.privateGroups
							}
						}
					);
				},
				(err) => {
					notification.showLocalizedMessage({
						level: 'error',
						titleKey: 'unexpected-error',
						messageKey: err.data.messageKey
					});
				}
			);
		}
	}, [props.show]);

	return (
		<>
			<Modal
				show={props.show}
				onHide={() => props.close()}
				size={'xl'}
			>
				<Modal.Header closeButton>
					<Modal.Title style={{width: 'auto'}}>
						{i18n.t('foedus-change-recipient-groups')}
					</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<SwEmbeddedDetail
						name='change-recipients'
						rest='/api/foedus/organ-offer/change-recipients'
						createButtonLabelKey='foedus-change-recipient-groups-button'
						restId={'new'}
						tabName={''}
						newCallback={(actionResult, id, errorResponse) => {
							if (!errorResponse) {
								props.close();
								window.location.reload();
							}
						}}
					>
						<SwForm
							name='change-recipients'
							rest={''}
							labelKey='foedus-organ-offer-info'
						>
							<SwForm.Section>
								<SwForm.Section type='tabs' labelKey='foedus-countries'>
									<SwForm.Embeddables
										size={12}
										property='notificationGroups.countries'
										label=' '
									>

										<SwForm.EmbeddablesList
											name='countries'
										>
											<SwList.Table>
												<SwList.Custom
													width='26px'
													property='checked'
													label=' '
													orderable={false}
													renderHeaderCell={() => {
														const allCountriesSelected = props?.newOrganOfferFormData?.notificationGroups?.countries?.every(country =>
															getSelectedOrganizationsInCountryCount(country) === getTotalOrganizationsInCountryCount(country));
														const someCountriesSelected = props?.newOrganOfferFormData?.notificationGroups?.countries?.some(country =>
															getSelectedOrganizationsInCountryCount(country) > 0);

														return (
															<IndeterminateCheckbox
																checked={allCountriesSelected}
																indeterminate={someCountriesSelected && !allCountriesSelected}
																onChange={onCheckedAllChange}
															/>
														);
													}}
													render={(resource) => {
														const allOrganizationsSelected = getSelectedOrganizationsInCountryCount(resource) === getTotalOrganizationsInCountryCount(resource) && getTotalOrganizationsInCountryCount(resource) !== 0;
														const someOrganizationsSelected = getSelectedOrganizationsInCountryCount(resource) > 0;

														return (
															<IndeterminateCheckbox
																checked={allOrganizationsSelected}
																indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																onChange={(e) => onCountryCheckedChange(resource, e.target.checked)}
															/>
														);
													}}
												/>
												<SwList.Custom
													property='country'
													labelKey='foedus-country'
													render={resource => {
														return (
															<div className="d-flex align-items-center">
																<CountryWithFlag
																	code={resource?.code}
																	name={resource?.nameEng ?? resource?.name}
																/>
																&nbsp;{getSelectedOrganizationsInCountryCount(resource)} / {getTotalOrganizationsInCountryCount(resource)}
															</div>

														);
													}}
												/>

												<SwList.Custom
													property='organizations'
													labelKey='core-organizations'
													render={resource => {
														return (
															<div className="d-flex align-items-center">
																{props?.newOrganOfferFormData?.selectedOrganizations
																	?.filter(so => so?.organization?.code === resource?.code)
																	?.map((o, index, array) => o?.name + (index < array.length - 1 ? ', ' : ''))}
															</div>
														);
													}}
												/>
											</SwList.Table>
										</SwForm.EmbeddablesList>

									</SwForm.Embeddables>
								</SwForm.Section>

								<SwForm.Section type='tabs' labelKey='core-organizations'>
									<SwForm.Embeddables
										size={12}
										property='notificationGroups.organizations'
										label=' '
									>
										<SwForm.EmbeddablesList
											name='organizations'
											defaultOrdering={[{property: 'organization.name', order: 'asc'}]}
										>

											<SwList.Table>
												<SwList.Custom
													width='26px'
													property='checked'
													label=' '
													orderable={false}
													renderHeaderCell={() => (
														<Form.Check
															checked={props?.newOrganOfferFormData?.selectedOrganizations?.length === props?.newOrganOfferFormData?.notificationGroups?.organizations?.length}
															onChange={onCheckedAllChange}
														/>
													)}
													render={resource => {
														return (
															<Form.Check
																checked={props?.newOrganOfferFormData?.selectedOrganizations?.some(so => so?.code === resource?.code)}
																onChange={e => onOrganizationCheckedChange(resource, e?.target?.checked)}
															/>
														);
													}}
												/>
												<SwList.Custom
													property='country'
													labelKey='foedus-country'
													render={resource => {
														return (
															<>
																<div className="d-flex align-items-center">
																	<CountryWithFlag
																		code={resource?.organization?.code}
																		name={resource?.organization?.nameEng ?? resource?.organization?.name}
																	/>
																</div>
															</>
														);
													}}
												/>

												<SwList.Custom
													property='organization'
													labelKey='core-organization'
													render={resource => resource?.nameEng ?? resource?.name}
												/>
											</SwList.Table>
										</SwForm.EmbeddablesList>
									</SwForm.Embeddables>

								</SwForm.Section>

								<SwForm.Section type='tabs' labelKey='foedus-global-groups'>
									<SwForm.Embeddables
										size={12}
										property='notificationGroups.globalGroups'
										label=' '
									>
										<SwForm.EmbeddablesList
											name='globalGroups'
											defaultOrdering={[{property: 'organization.name', order: 'asc'}]}
										>

											<SwList.Table>
												<SwList.Custom
													width='26px'
													property='checked'
													label=' '
													orderable={false}
													renderHeaderCell={() => {
														const allGlobalGroupsSelected = props?.newOrganOfferFormData?.notificationGroups?.globalGroups?.every(globalGroup =>
															getSelectedOrganizationsInGlobalGroupCount(globalGroup) === getTotalOrganizationsInGlobalGroupCount(globalGroup));
														const someGlobalGroupSelected = props?.newOrganOfferFormData?.notificationGroups?.globalGroups?.some(globalGroup =>
															getSelectedOrganizationsInGlobalGroupCount(globalGroup) > 0);

														return (
															<IndeterminateCheckbox
																checked={allGlobalGroupsSelected}
																indeterminate={someGlobalGroupSelected && !allGlobalGroupsSelected}
																onChange={onCheckedAllGlobalGroupsChange}
															/>
														);
													}}
													render={resource => {
														const allOrganizationsSelected = getSelectedOrganizationsInGlobalGroupCount(resource) === getTotalOrganizationsInGlobalGroupCount(resource) && getTotalOrganizationsInGlobalGroupCount(resource) !== 0;
														const someOrganizationsSelected = getSelectedOrganizationsInGlobalGroupCount(resource) > 0;

														return (
															<IndeterminateCheckbox
																checked={allOrganizationsSelected}
																indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																onChange={(event) => onGlobalGroupCheckedChange(resource, event?.target?.checked)}
															/>
														);
													}}
												/>
												<SwList.Custom
													property='name'
													labelKey='foedus-name'
													render={resource => (
														<div className="d-flex align-items-center">
															{resource?.name}
															&nbsp;{getSelectedOrganizationsInGlobalGroupCount(resource)} / {getTotalOrganizationsInGlobalGroupCount(resource)}
														</div>
													)}
												/>

												<SwList.Custom
													property='organizations'
													labelKey='core-organizations'
													render={resource => <OrganizationsList
														organizations={resource?.organizations}/>}
												/>
											</SwList.Table>
										</SwForm.EmbeddablesList>

									</SwForm.Embeddables>
								</SwForm.Section>

								<SwForm.Section type='tabs' labelKey='foedus-private-groups'>
									<SwForm.Embeddables
										size={12}
										property='notificationGroups.privateGroups'
										label=' '
									>
										<SwForm.EmbeddablesList
											name='privateGroups'
											defaultOrdering={[{property: 'organization.name', order: 'asc'}]}
										>

											<SwList.Table>
												<SwList.Custom
													width='26px'
													property='checked'
													label=' '
													orderable={false}
													renderHeaderCell={() => {
														const allPrivateGroupsSelected = props?.newOrganOfferFormData?.notificationGroups?.privateGroups?.every(privateGroup =>
															getSelectedOrganizationsInGlobalGroupCount(privateGroup) === getTotalOrganizationsInGlobalGroupCount(privateGroup));
														const somePrivateGroupSelected = props?.newOrganOfferFormData?.notificationGroups?.privateGroups?.some(privateGroup =>
															getSelectedOrganizationsInGlobalGroupCount(privateGroup) > 0);

														return (
															<IndeterminateCheckbox
																checked={allPrivateGroupsSelected}
																indeterminate={somePrivateGroupSelected && !allPrivateGroupsSelected}
																onChange={onCheckedAllPrivateGroupsChange}
															/>
														);
													}}
													render={resource => {
														const allOrganizationsSelected = getSelectedOrganizationsInPrivateGroupCount(resource) === getTotalOrganizationsInPrivateGroupCount(resource) && getTotalOrganizationsInPrivateGroupCount(resource) !== 0;
														const someOrganizationsSelected = getSelectedOrganizationsInPrivateGroupCount(resource) > 0;

														return (
															<IndeterminateCheckbox
																checked={allOrganizationsSelected}
																indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																onChange={(event) => onPrivateGroupCheckedChange(resource, event?.target?.checked)}
															/>
														);
													}}
												/>
												<SwList.Custom
													property='name'
													labelKey='foedus-name'
													render={resource => (
														<div className="d-flex align-items-center">
															{resource?.name}
															&nbsp;{getSelectedOrganizationsInPrivateGroupCount(resource)} / {getTotalOrganizationsInPrivateGroupCount(resource)}
														</div>
													)}
												/>

												<SwList.Custom
													property='organizations'
													labelKey='core-organizations'
													render={resource => <OrganizationsList
														organizations={resource?.organizations}/>}
												/>
											</SwList.Table>
										</SwForm.EmbeddablesList>

									</SwForm.Embeddables>
								</SwForm.Section>
							</SwForm.Section>

							<SwForm.Section>
								<SwForm.Custom
									property='selectedOrganizations'
									labelKey='foedus-selected-organizations'
									render={(resource) => <SelectedOrganizationsList
										selectedOrganizations={resource?.selectedOrganizations}/>}
								/>
							</SwForm.Section>

						</SwForm>
					</SwEmbeddedDetail>
				</Modal.Body>
			</Modal>
		</>

	);
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		detail: bindActionCreators(DetailActions, dispatch)
	}
});

const mapStateToProps = (state, ownProps) => {
	const newOrganOfferFormData = ComponentUtils.getFormData(state, '/api/foedus/organ-offer/change-recipients/new', 'DEFAULT');
	const pathSegments = state?.router?.location?.pathname?.split('/') || [];

	return {
		organOfferAttachmentTypeCbIds: state?.foedus?.organOfferAttachmentTypeCbIds,
		loggedUserPrimaryFoedusRole: state?.foedus?.loggedUserPrimaryFoedusRole,
		loggedUserCountry: state?.foedus?.loggedUserCountry,
		countryCoreCodebook: state?.foedus?.countryCoreCodebook,
		nationalBodyCoreCodebook: state?.foedus?.nationalBodyCoreCodebook,
		newOrganOfferFormData,
		router: state?.router,
		entryId: getElementAfter(pathSegments, 'entries'),
		definitionId: getElementAfter(pathSegments, 'definition')
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeRecipientsModal);
